import React, { useState, useCallback, useMemo, useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useTranslation } from 'localization';
import type { Context } from 'types/context';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAvailableContexts } from '../../../hooks/useAvailableContexts';
import './ContextSelectorPopoverMolecule.scss';

const DEFAULT_CONTEXT_SYSTEM_NAME = 'center_armillary_context';

export interface ContextSelectorPopoverMoleculeProps {
  /** Sphere whose AVAILABLE contexts are offered (deployed-approach providedContexts + built-ins, applicability-gated server-side). */
  sphereId: string | null;
  activeContextSystemName: string;
  onContextSelect: (systemName: string, displayName: string) => void;
}

interface ContextListItem {
  systemName: string;
  displayName: string;
  isDefault: boolean;
}

/**
 * Inner content component that only mounts when the OverlayPanel is visible.
 * This avoids unnecessary API calls when the popover is closed.
 *
 * Offers the sphere's AVAILABLE contexts (GET /spheres/:id/available-contexts):
 * contexts provided by approaches deployed on the sphere or its ancestors,
 * plus built-ins, with applicability evaluated server-side. This keeps the
 * offering scoped to where each context's providing approach is deployed —
 * a fleet approach's contexts appear on the fleet subtree, not on unrelated
 * departments.
 */
const ContextSelectorContent: React.FC<{
  sphereId: string | null;
  activeContextSystemName: string;
  onContextSelect: (systemName: string, displayName: string) => void;
  onClose: () => void;
}> = ({ sphereId, activeContextSystemName, onContextSelect, onClose }) => {
  const { t, i18n } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { contexts: availableContexts, loading, error: fetchError } = useAvailableContexts(
    apiClients,
    sphereId
  );

  const getLocalizedName = useCallback(
    (context: Context): string => {
      const lang = i18n.language;
      if (context.name.translations?.[lang]) {
        return context.name.translations[lang];
      }
      return context.name.default;
    },
    [i18n.language]
  );

  const contexts = useMemo((): ContextListItem[] => {
    const items: ContextListItem[] = availableContexts.map((c) => ({
      systemName: c.system_name,
      displayName:
        c.system_name === DEFAULT_CONTEXT_SYSTEM_NAME
          ? t('contextSelector.default')
          : getLocalizedName(c),
      isDefault: c.system_name === DEFAULT_CONTEXT_SYSTEM_NAME,
    }));

    // Sort: default first, then alphabetical
    items.sort((a, b) => {
      if (a.isDefault) return -1;
      if (b.isDefault) return 1;
      return a.displayName.localeCompare(b.displayName);
    });

    return items;
  }, [availableContexts, t, getLocalizedName]);

  const error = fetchError ? t('contextSelector.errorLoading') : null;

  const handleItemClick = useCallback(
    (item: ContextListItem) => {
      onContextSelect(item.systemName, item.displayName);
      onClose();
    },
    [onContextSelect, onClose]
  );

  const handleItemKeyDown = useCallback(
    (e: React.KeyboardEvent, item: ContextListItem) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onContextSelect(item.systemName, item.displayName);
        onClose();
      }
    },
    [onContextSelect, onClose]
  );

  if (loading) {
    return (
      <div className="context-selector-popover__loading">
        <i className="pi pi-spin pi-spinner" />
        <span>{t('contextSelector.loadingContexts')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="context-selector-popover__error">
        <i className="pi pi-exclamation-triangle" />
        <span>{error}</span>
      </div>
    );
  }

  const nonDefaultCount = contexts.filter((c) => !c.isDefault).length;

  return (
    <>
      <div className="context-selector-popover__header">
        {t('contextSelector.header')}
      </div>
      <ul className="context-selector-popover__list" role="listbox">
        {contexts.map((item) => {
          const isActive = item.systemName === activeContextSystemName;
          return (
            <li
              key={item.systemName}
              className={`context-selector-popover__item ${isActive ? 'context-selector-popover__item--active' : ''}`}
              role="option"
              aria-selected={isActive}
              tabIndex={0}
              onClick={() => handleItemClick(item)}
              onKeyDown={(e) => handleItemKeyDown(e, item)}
            >
              <span className="context-selector-popover__check">
                {isActive && <i className="pi pi-check" />}
              </span>
              <span className="context-selector-popover__item-name">
                {item.displayName}
              </span>
            </li>
          );
        })}
      </ul>
      {nonDefaultCount === 0 && (
        <div className="context-selector-popover__empty">
          {t('contextSelector.noOtherContexts')}
        </div>
      )}
    </>
  );
};

/**
 * ContextSelectorPopoverMolecule - Popover for switching between applicable contexts.
 *
 * Uses forwardRef to expose the OverlayPanel ref for external toggle
 * (following the same pattern as StatusPopoverMolecule).
 */
const ContextSelectorPopoverMolecule = React.forwardRef<
  OverlayPanel,
  ContextSelectorPopoverMoleculeProps
>(({ sphereId, activeContextSystemName, onContextSelect }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const internalRef = useRef<OverlayPanel>(null);
  const overlayRef = (ref as React.RefObject<OverlayPanel>) || internalRef;

  const handleShow = useCallback(() => {
    setIsVisible(true);
  }, []);

  const handleHide = useCallback(() => {
    setIsVisible(false);
  }, []);

  const handleClose = useCallback(() => {
    overlayRef.current?.hide();
  }, []);

  return (
    <OverlayPanel
      ref={overlayRef}
      className="context-selector-popover"
      onShow={handleShow}
      onHide={handleHide}
      dismissable
      appendTo={document.body}
    >
      {isVisible && (
        <ContextSelectorContent
          sphereId={sphereId}
          activeContextSystemName={activeContextSystemName}
          onContextSelect={onContextSelect}
          onClose={handleClose}
        />
      )}
    </OverlayPanel>
  );
});

ContextSelectorPopoverMolecule.displayName = 'ContextSelectorPopoverMolecule';

export default ContextSelectorPopoverMolecule;
